import { Badge, Button, Link as MuiLink, useMediaQuery } from "@mui/material";
import { usePaymentActions } from "hooks/usePaymentActions";
import { Trans, useTranslation } from "react-i18next";
import { ConfirmDialog, Drawer, IconButton } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { BubbleChatIcon } from "@hugeicons-pro/core-stroke-rounded";
import StartPaymentDetails from "./StartPaymentDetails";
import React, { useEffect, useState } from "react";
import EditPaymentDetails from "./EditPaymentDetails";
import PaymentDetailsSummary from "./PaymentDetailsSummary";
import { useComments } from "hooks/useComments";
import CommentsPanel from "components/common/comments/CommentsPanel";
import { ParentType } from "enums/Comments";
import {
  LocalImageAsset,
  OcrInvoiceResult,
} from "hooks/useAttachmentAndAssetHandling";
import {
  canSavePaymentAsDraft,
  FormikPayment,
} from "utils/payments/paymentFormat";
import PaymentStatus from "enums/PaymentStatus";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import { InviteContactModal } from "../contacts/ContactModules";
import useGetBankAccounts from "hooks/useGetBankAccounts";
import AccountType from "enums/AccountType";

// The DS drawer's own default width. Mirrored here only because the comments
// panel docks flush against the paper edge and has to know where it is.
const DRAWER_WIDTH = 420;

interface EditPaymentsDrawerProps {
  open: boolean;
  paymentId?: string;
  reusePaymentValues?: FormikPayment;
  newPayment?: boolean;
  handleClosePaymentDrawerRouter: (
    refresh?: boolean,
    idToOpenPaymentDetails?: string,
  ) => void;
}

const PaymentDrawerRouter = (props: EditPaymentsDrawerProps) => {
  const {
    open,
    paymentId,
    reusePaymentValues,
    newPayment,
    handleClosePaymentDrawerRouter,
  } = props;
  const { t } = useTranslation();

  const [step, setStep] = useState<"start" | "edit" | "summary">("start");
  const [unsavedChangesModal, setUnsavedChangesModal] = useState(false);
  const [ocrResult, setOcrResult] = useState<OcrInvoiceResult | null>(null);
  const [contactHasPaymentAccount, setContactHasPaymentAccount] =
    useState<boolean>(false);
  const [openInviteContactModal, setOpenInviteContactModal] =
    useState<boolean>(false);
  const [
    paymentContactMissingBankErrorMessage,
    setInviteContactMissingBankErrorMessage,
  ] = useState<React.ReactNode | null>(null);
  const [
    refetchSelectedPayToContactIndex,
    setRefetchSelectedPayToContactIndex,
  ] = useState<number>(0);

  // The panel docks beside the drawer only when there is room for both.
  const commentsDockable = useMediaQuery(`(min-width:${DRAWER_WIDTH + 520}px)`);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [nestedComments, setNestedComments] = useState(false);
  const showComments = commentsOpen && !nestedComments;

  // Empty until the payment exists: `Comments` then holds the draft and the
  // router submits it through `submitDraftCommentIfNeeded` after the save.
  const commentActions = useComments(
    open ? paymentId || "" : "",
    ParentType.PAYMENT,
  );
  const commentCount = commentActions.comments?.length ?? 0;

  const {
    formikPayment,
    customFieldSettings,
    specificLoadingStates,
    paymentInitialValues,
    handleSavePayment,
    resetFormWithPayment,
  } = usePaymentActions({
    paymentId,
    shouldFetchPayments: open,
  });

  const { validateAndTouch } = useFormikHelpers(formikPayment);

  const { internalAccounts, isLoading: loadingBankAccounts } =
    useGetBankAccounts({
      accountTypes: [AccountType.FINANCIAL_ACCOUNT],
      skip: !open,
      withBalances: false,
    });

  const bankAccountData = internalAccounts[0];

  const handleLocalFileUpload = (
    files: LocalImageAsset[],
    runOCR?: boolean,
  ) => {
    const newAttachments = files.map((file) => ({
      id: file?.id,
      assetUrl: file?.assetUrl,
      extension: file?.extension,
      file: file?.file,
    }));

    formikPayment.setFieldValue("attachments", [
      ...formikPayment.values.attachments,
      ...newAttachments,
    ]);
  };

  const handleRemoveAsset = (assetId: string) => {
    formikPayment.setFieldValue(
      "attachments",
      formikPayment.values.attachments.filter((asset) => asset.id !== assetId),
    );
  };

  useEffect(() => {
    if (paymentId) {
      setStep("edit");
      // Do NOT reset form here!
    }

    if (newPayment) {
      formikPayment.resetForm({ values: paymentInitialValues });
      setOcrResult(null);
      setStep("start");
      setRefetchSelectedPayToContactIndex(0);
    }

    if (!open) {
      setOcrResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentId, open, paymentInitialValues]);

  useEffect(() => {
    if (reusePaymentValues) {
      formikPayment.resetForm({ values: reusePaymentValues });
      setStep("edit");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reusePaymentValues]);

  const refetchSelectedPayToContact = () => {
    setRefetchSelectedPayToContactIndex((prev) => prev + 1);
  };

  useEffect(() => {
    const payToId = formikPayment.values?.payTo?.id;
    if (!payToId) {
      setContactHasPaymentAccount(false);
      setOpenInviteContactModal(false);
      setInviteContactMissingBankErrorMessage(null);
      return;
    }

    const hasBank = !!formikPayment.values?.payTo?.hasPaymentAccount;
    setContactHasPaymentAccount(hasBank);

    if (payToId && !hasBank) {
      setOpenInviteContactModal(true);
      setInviteContactMissingBankErrorMessage(
        <Trans
          i18nKey="PaymentContactMissingBankError"
          components={{
            invite_link: (
              <MuiLink
                component="button"
                type="button"
                onClick={() => setOpenInviteContactModal(true)}
              />
            ),
          }}
        />,
      );
    } else {
      setInviteContactMissingBankErrorMessage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formikPayment.values?.payTo?.id,
    formikPayment.values?.payTo?.hasPaymentAccount,
  ]);

  const closePaymentDrawer = (
    refresh?: boolean,
    idToOpenPaymentDetails?: string,
    skipDirty?: boolean,
  ) => {
    if (formikPayment.dirty && !skipDirty) {
      setUnsavedChangesModal(true);
      return;
    }

    handleClosePaymentDrawerRouter(refresh, idToOpenPaymentDetails);
  };

  const submitDraftCommentIfNeeded = async (newPaymentId: string) => {
    if (paymentId) return;
    if (!commentActions.hasDraft) return;
    await commentActions.submit(newPaymentId);
  };

  // Next step logic
  const handleNextStep = async () => {
    if (paymentId || step === "summary") {
      // Validate before saving if editing
      if (!(await validateAndTouch())) return;
      const result = await handleSavePayment();
      if (result) {
        await submitDraftCommentIfNeeded(result);
        closePaymentDrawer(true, result, true);
      }
    } else if (step === "start") {
      // Only block if payTo.id is missing
      if (!formikPayment.values?.payTo?.id) {
        await validateAndTouch({ payTo: { id: true } });
        return;
      }
      setStep("edit");
    } else if (step === "edit") {
      if (!(await validateAndTouch())) return;
      setStep("summary");
    }
  };

  // Back step logic
  const handlePreviousStep = () => {
    if (paymentId || step === "start") {
      closePaymentDrawer();
    } else if (step === "edit") {
      setStep("start");
    } else if (step === "summary") {
      setStep("edit");
    }
  };

  const stepTitles: Record<string, string> = {
    start: t("Start Send a Payment"),
    edit: t("Send a Payment"),
    summary: t("Send a Payment Summary"),
  };

  const paymentDrawerTitle = paymentId
    ? t("Edit Payment")
    : t(stepTitles[step]);

  const busy =
    specificLoadingStates.savingPayment ||
    specificLoadingStates.isLoadingAttachments ||
    specificLoadingStates.isUploadingAttachments ||
    commentActions.addingComment;

  return (
    <>
      <Drawer
        open={open}
        onClose={() => closePaymentDrawer()}
        onAfterClose={() => setCommentsOpen(false)}
        title={t(paymentDrawerTitle)}
        loading={busy}
        disableEnforceFocus={showComments}
        headerAction={
          <IconButton
            variant="ghost"
            aria-label={t("Comments")}
            onClick={() => setCommentsOpen((wasOpen) => !wasOpen)}
          >
            <Badge badgeContent={commentCount} color="primary">
              <HugeiconsIcon icon={BubbleChatIcon} size={18} />
            </Badge>
          </IconButton>
        }
        footer={
          <>
            <Button variant="primary-outlined" onClick={handlePreviousStep}>
              {paymentId || step === "start" ? t("Close") : t("Back")}
            </Button>

            {step === "edit" &&
              canSavePaymentAsDraft(
                formikPayment.values.id,
                formikPayment.values.status as PaymentStatus,
              ) && (
                <Button
                  variant="secondary-filled"
                  onClick={async () => {
                    await formikPayment.setFieldValue(
                      "status",
                      PaymentStatus.DRAFT,
                    );
                    const result = await handleSavePayment(PaymentStatus.DRAFT);
                    if (result) {
                      await submitDraftCommentIfNeeded(result);
                      closePaymentDrawer(true, result, true);
                    }
                  }}
                  disabled={
                    !formikPayment.values.payTo?.id ||
                    specificLoadingStates.savingPayment ||
                    commentActions.addingComment
                  }
                >
                  {t("Save as Draft")}
                </Button>
              )}

            <Button
              variant="primary-filled"
              onClick={handleNextStep}
              disabled={
                specificLoadingStates.savingPayment ||
                commentActions.addingComment
              }
            >
              {paymentId || step === "summary" ? t("Submit") : t("Next")}
            </Button>
          </>
        }
      >
        {step === "start" && (
          <StartPaymentDetails
            formikPayment={formikPayment}
            ocrResult={ocrResult}
            paymentContactMissingBankErrorMessage={
              paymentContactMissingBankErrorMessage
            }
            refetchSelectedPayToContactIndex={refetchSelectedPayToContactIndex}
            handleLocalFileUpload={handleLocalFileUpload}
            handleNext={handleNextStep}
            setOcrResult={setOcrResult}
            resetFormWithPayment={resetFormWithPayment}
          />
        )}

        {step === "edit" && (
          <EditPaymentDetails
            formikPayment={formikPayment}
            customFieldSettings={customFieldSettings}
            loadingBankAccountDetails={loadingBankAccounts}
            bankAccountData={bankAccountData}
            paymentContactMissingBankErrorMessage={
              paymentContactMissingBankErrorMessage
            }
            refetchSelectedPayToContactIndex={refetchSelectedPayToContactIndex}
            handleLocalFileUpload={handleLocalFileUpload}
            handleRemoveAttachments={handleRemoveAsset}
          />
        )}

        {step === "summary" && (
          <PaymentDetailsSummary
            formikPayment={formikPayment}
            customFieldSettings={customFieldSettings}
            handleLocalFileUpload={handleLocalFileUpload}
            handleRemoveAttachments={handleRemoveAsset}
            onNestedCommentsChange={setNestedComments}
          />
        )}
      </Drawer>

      <CommentsPanel
        open={open && showComments}
        onClose={() => setCommentsOpen(false)}
        drawerWidth={DRAWER_WIDTH}
        docked={commentsDockable}
        commentActions={commentActions}
      />

      <ConfirmDialog
        open={unsavedChangesModal}
        tone="primary"
        title={t("Unsaved Changes")}
        message={t("UnsavedChangesDescription")}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        onConfirm={() => {
          setUnsavedChangesModal(false);
          handleClosePaymentDrawerRouter();
        }}
        onCancel={() => setUnsavedChangesModal(false)}
      />

      {openInviteContactModal && formikPayment.values?.payTo?.id && (
        <InviteContactModal
          openInviteContactModal={openInviteContactModal}
          setOpenInviteContactModal={() => setOpenInviteContactModal(false)}
          contact={{
            id: formikPayment.values.payTo.id,
            email: formikPayment.values.payTo.email,
            contactType: formikPayment.values.payTo.type,
          }}
          isMissingBank={!contactHasPaymentAccount}
          refetchSelectedPayToContact={refetchSelectedPayToContact}
        />
      )}
    </>
  );
};

export default PaymentDrawerRouter;
